<template>
  <div>
    <div class="px-4 py-2 flex items-center justify-between mb-4">
      <h3
        id="menus.title"
        class="text-lg text-center font-medium text-gray-900 flex-grow"
      >
        {{ translate("Menus") }}
      </h3>
      <button
        id="menus.opencreatemenumodal"
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        @click="isNewMenuModalOpen = true"
      >
        <i class="ni ni-fat-add mr-2"></i>
        <span>{{ translate("New Menu") }}</span>
      </button>
    </div>

    <div class="container mx-auto px-4">
      <!-- Menu List Component -->
      <menu-list
        :list="list"
        :loading="loading"
        @deleteMenu="confirmAction('delete', $event)"
        @getMenus="getMenus"
        @syncPOS="syncPOS"
        @hardReset="confirmAction('hardReset', $event)"
      ></menu-list>
    </div>

    <!-- Modals -->

    <MenuModal
      v-if="isNewMenuModalOpen"
      :isOpen="isNewMenuModalOpen"
      @close="isNewMenuModalOpen = false"
      @getMenus="getMenus"
    />

    <!-- Generic Confirmation Modal -->
    <BaseModal
      v-if="showConfirmModal"
      :title="confirmationConfig.title"
      @close="cancelAction"
      headerClass="py-3"
    >
      <template v-slot:body>
        <div class="flex items-center py-3">
          <div
            class="mr-2 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full"
            :class="confirmationConfig.iconBgClass"
          >
            <i :class="confirmationConfig.iconClass"></i>
          </div>
          <div class="text-center sm:text-left">
            <p class="text-lg mb-0 font-medium">
              {{ confirmationConfig.message }}
            </p>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div class="w-full flex justify-end py-3">
          <button
            :id="confirmationConfig.cancelButtonId"
            @click="cancelAction"
            class="rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {{ translate("Cancel") }}
          </button>
          <button
            :id="confirmationConfig.confirmButtonId"
            @click="executeAction"
            class="rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            :class="confirmationConfig.confirmButtonClass"
          >
            {{ confirmationConfig.confirmButtonText }}
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { eventHandler, translations } from '@/mixins';
import MenuList from './components/MenuList';
import MenuModal from './components/MenuModal.vue';
import {
  Integrations as IntegrationsApi,
  Menu as MenuApi,
} from '@/services/SOLO';
import { BaseModal } from '@/components';

export default {
  name: 'MenusLanding',

  components: {
    MenuList,
    MenuModal,
    BaseModal,
  },

  mixins: [eventHandler, translations],

  computed: {
    ...mapGetters({
      getLocale: 'app/getLocale',
    }),

    confirmationConfig () {
      const configs = {
        delete: {
          title: this.translate('Delete Menu'),
          message: this.translate('Are you sure you want to delete this menu?'),
          iconClass: 'fas fa-trash-alt text-red-600',
          iconBgClass: 'bg-red-100',
          confirmButtonText: this.translate('Delete'),
          confirmButtonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          confirmButtonId: 'menus.deletemenu',
          cancelButtonId: 'menus.closedeletemenumodal',
        },
        hardReset: {
          title: this.translate('Hard Reset Menu'),
          message: this.translate('Are you sure you want to hard reset this menu? This action cannot be undone.'),
          iconClass: 'fas fa-exclamation-triangle text-orange-600',
          iconBgClass: 'bg-gray-100',
          confirmButtonText: this.translate('Hard Reset'),
          confirmButtonClass: 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500',
          confirmButtonId: 'menus.hardresetmenu',
          cancelButtonId: 'menus.closehardresetmodal',
        },
      };
      return configs[this.currentAction] || configs.delete;
    },
  },

  data () {
    return {
      isNewMenuModalOpen: false,
      list: [],
      loading: false,
      page: 1,
      showConfirmModal: false,
      currentAction: null,
      menuId: null,
    };
  },

  mounted () {
    this.getMenus(this.page);
  },

  methods: {
    async getMenus (page = 1) {
      this.loading = true;
      try {
        const response = await MenuApi.all(page);
        this.list = response?.data?.data;
      } catch (error) {
        console.error('Error fetching menus:', error);
      } finally {
        this.loading = false;
      }
    },

    confirmAction (action, menuId) {
      this.currentAction = action;
      this.menuId = menuId;
      this.showConfirmModal = true;
    },

    cancelAction () {
      this.showConfirmModal = false;
      this.currentAction = null;
      this.menuId = null;
    },

    async syncPOS (menuId) {
      try {
        await IntegrationsApi.sync('pos', menuId);
        this.successToast('MENU SYNCED!', 'Menu has been synced successfully');
      } catch (error) {
        this.errorToast(
          'MENU SYNC ERROR!',
          'Menu has not been synced successfully',
        );
      }
    },

    async executeAction () {
      if (!this.menuId) return;

      try {
        if (this.currentAction === 'delete') {
          await MenuApi.deleteMenu(this.menuId);
          this.successToast(
            'MENU DELETED!',
            'Menu has been deleted successfully',
          );
        } else if (this.currentAction === 'hardReset') {
          await MenuApi.hardReset(this.menuId);
          this.successToast(
            'MENU HARD RESET!',
            'Menu has been hard reset successfully',
          );
        }
        this.getMenus(this.page);
      } catch (error) {
        const errorMessages = {
          delete: {
            title: 'SYSTEM ERROR!',
            message: 'Something went wrong, please try again!',
          },
          hardReset: {
            title: 'MENU HARD RESET ERROR!',
            message: 'Menu has not been hard reset successfully',
          },
        };

        const errorConfig = errorMessages[this.currentAction] || errorMessages.delete;
        this.errorToast(errorConfig.title, errorConfig.message);
      } finally {
        this.showConfirmModal = false;
        this.currentAction = null;
        this.menuId = null;
      }
    },

    successToast (title, message) {
      this.$notify({
        title: title,
        verticalAlign: 'bottom',
        horizontalAlign: 'left',
        message: message,
        type: 'success',
        icon: 'fas fa-check',
      });
    },

    errorToast (title, message) {
      this.$notify({
        title: title,
        verticalAlign: 'bottom',
        horizontalAlign: 'left',
        message: message,
        type: 'danger',
        icon: 'fas fa-bomb',
      });
    },
  },
};
</script>
